// Loads: money put onto cards (console, points, kiosks), the ones that need someone first. A load above the program's
// approval threshold waits here for a second admin — never the one who asked; a load the processor never answered is
// "Unknown": the money may or may not have moved, so it is checked with the processor, never loaded again.
// Added: unloads (money taken back off a card) show here too, and a succeeded load can be reversed where the card's
// processor offers that. A kiosk load shows its terminal id.
import { money, shortDate, words } from '@qor/web-shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, fundsApi, totalPages } from '../api';
import { usePageTitle } from '../components/ConsoleLayout';
import { toQuery, useFilterForm, useQueryParams } from '../components/filters';
import { useStaff } from '../components/RequireStaff';
import { confirmed, PageHeading, Pagination, ResultBanner, StatusBadge } from '../components/ui';

const PAGE_SIZE = 20;
export const LOAD_STATUSES = ['PendingApproval', 'Unknown', 'Succeeded', 'Failed', 'Rejected', 'Submitted'] as const;

/** "PendingApproval" -> "pending_approval", as the API filters. */
const apiStatus = (status: string) => status.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
export const LOAD_CHANNELS = [
  ['admin', 'Console'],
  ['kiosk', 'Kiosk'],
  ['points_redemption', 'Points redemption'],
] as const;
const channelLabel = (channel: string) => LOAD_CHANNELS.find(([key]) => key === channel)?.[1] ?? channel;

const pascalStatus = (status: string) => status.replace(/(^|_)([a-z])/g, (_, __: string, c: string) => c.toUpperCase());

export function LoadsPage() {
  usePageTitle('Loads');
  const { user } = useStaff();
  const client = useQueryClient();
  const q = useQueryParams();
  // the queue is what this page is for: without a filter, the loads waiting for a second admin
  const status = q.get('status') ?? 'PendingApproval';
  const channel = q.get('channel') ?? '';
  const page = q.page();
  const submit = useFilterForm('/loads');
  const applyNow = (e: ChangeEvent<HTMLSelectElement>) => e.currentTarget.form?.requestSubmit();
  const [outcome, setOutcome] = useState<{
    message: string | null;
    ok: boolean;
  }>({ message: null, ok: false });
  const [busy, setBusy] = useState(false);
  const results = useQuery({
    queryKey: ['loads', status, channel, page],
    queryFn: () =>
      adminApi.loads({
        status: status === 'All' ? null : apiStatus(status),
        channel: channel || null,
        page,
        pageSize: PAGE_SIZE,
      }),
  });
  const data = results.data;
  const mayDecide = user.role === 'SystemAdmin' || user.role === 'ProgramAdmin';
  const mayResolve = user.role === 'SystemAdmin' && user.internal;

  async function decide(id: string, approve: boolean) {
    let note: string | null = null;
    if (approve) {
      if (!confirmed('Approve this load? The money goes onto the card now.')) return;
    } else {
      const answer = window.prompt('Reject this load? Say why (optional):');
      if (answer === null) return;
      note = answer.trim() || null;
    }
    setBusy(true);
    const r = approve ? await adminApi.approveLoad(id) : await adminApi.rejectLoad(id, note);
    setBusy(false);
    setOutcome({
      ok: r.success,
      message: r.success ? (approve ? 'Load approved.' : 'Load rejected.') : (r.error ?? "Couldn't do that."),
    });
    await client.invalidateQueries({ queryKey: ['loads'] });
  }

  /** A load the processor never answered, once checked with the processor (QOR system admins). */
  async function resolve(id: string, succeeded: boolean) {
    const note = window.prompt(
      succeeded
        ? 'The processor confirms the money reached the card. How did you check (required)?'
        : 'The processor confirms the money never reached the card. How did you check (required)?',
    );
    if (!note?.trim()) return;
    setBusy(true);
    const r = await fundsApi.resolveLoad(id, succeeded ? 'succeeded' : 'failed', note.trim(), null);
    setBusy(false);
    setOutcome({
      ok: r.ok,
      message: r.ok ? (succeeded ? 'Recorded as succeeded.' : 'Recorded as failed: its money is given back.') : r.error,
    });
    await client.invalidateQueries({ queryKey: ['loads'] });
  }

  async function reverse(id: string) {
    if (!confirmed('Reverse this load at the processor? The money comes back off the card.')) return;
    setBusy(true);
    const r = await adminApi.reverseLoad(id);
    setBusy(false);
    setOutcome({ ok: r.success, message: r.success ? 'Load reversed.' : (r.error ?? "Couldn't do that.") });
    await client.invalidateQueries({ queryKey: ['loads'] });
  }

  return (
    <>
      <PageHeading title="Loads" />
      <p className="qc-breadcrumb" style={{ marginBottom: 12 }}>
        Money put onto cards from the console, points and kiosks. A load above the program&rsquo;s approval threshold
        waits here for a second admin.
      </p>

      <ResultBanner {...outcome} />

      <form className="qc-cardholder-filters" onSubmit={submit} key={status + channel}>
        <select name="status" aria-label="Filter by status" defaultValue={status} onChange={applyNow}>
          {LOAD_STATUSES.map((s) => (
            <option key={s} value={s}>
              {words(s)}
            </option>
          ))}
          <option value="All">All loads</option>
        </select>
        <select name="channel" aria-label="Filter by channel" defaultValue={channel} onChange={applyNow}>
          <option value="">All channels</option>
          {LOAD_CHANNELS.map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </form>

      {status === 'Unknown' && (
        <p className="qc-auth-error" role="alert">
          The processor did not answer these loads: the money may or may not have moved. Check each one with the
          processor before doing anything else; never load again to &ldquo;fix&rdquo; one.
        </p>
      )}

      <div className="qc-panel">
        {!data ? null : data.items.length === 0 ? (
          <p className="qc-empty">
            {status === 'PendingApproval' ? 'No load is waiting for approval.' : 'No loads match this filter.'}
          </p>
        ) : (
          <>
            <table className="qc-plain-table">
              <thead>
                <tr>
                  <th>Requested</th>
                  <th>Cardholder</th>
                  <th>Channel</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((l) => {
                  const mine = l.requestedBy.id === user.id;
                  return (
                    <tr key={l.id}>
                      <td>{shortDate(l.createdAt)}</td>
                      <td>
                        <Link to={`/cardholders/${l.cardholderId}`}>Open cardholder</Link>
                      </td>
                      <td>
                        {channelLabel(l.channel)}
                        {l.terminalId && (
                          <>
                            <br />
                            <small>Terminal {l.terminalId}</small>
                          </>
                        )}
                        {l.direction === 'debit' && (
                          <>
                            <br />
                            <small>Unload</small>
                          </>
                        )}
                      </td>
                      <td>
                        {money(Number(l.amount), l.currency)} {l.currency}
                      </td>
                      <td>
                        <StatusBadge status={pascalStatus(l.status)} good="Succeeded" />
                        {l.message && (
                          <>
                            <br />
                            <small>{l.message}</small>
                          </>
                        )}
                        {l.reversedAt && (
                          <>
                            <br />
                            <small>Reversed {shortDate(l.reversedAt)}</small>
                          </>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {l.status === 'pending_approval' && mayDecide ? (
                          mine ? (
                            <small>Needs another admin</small>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="qor-button qor-button-primary"
                                disabled={busy}
                                onClick={() => void decide(l.id, true)}
                              >
                                Approve
                              </button>{' '}
                              <button
                                type="button"
                                className="qc-button-ghost"
                                disabled={busy}
                                onClick={() => void decide(l.id, false)}
                              >
                                Reject
                              </button>
                            </>
                          )
                        ) : l.status === 'unknown' && mayResolve ? (
                          <>
                            <button
                              type="button"
                              className="qc-button-ghost"
                              disabled={busy}
                              onClick={() => void resolve(l.id, true)}
                            >
                              It went through
                            </button>{' '}
                            <button
                              type="button"
                              className="qc-button-ghost"
                              disabled={busy}
                              onClick={() => void resolve(l.id, false)}
                            >
                              It did not
                            </button>
                          </>
                        ) : l.reversible && mayDecide ? (
                          <button
                            type="button"
                            className="qc-button-ghost"
                            disabled={busy}
                            onClick={() => void reverse(l.id)}
                          >
                            Reverse
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination
              page={data.page}
              totalPages={totalPages(data)}
              totalCount={data.totalCount}
              href={(p) => `/loads${toQuery({ status: status, page: p })}`}
            />
          </>
        )}
      </div>
    </>
  );
}
